import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { RangeValue } from '@react-types/shared';
import { uniq } from 'lodash/fp';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import DynamicInput from 'dashboard/components/DynamicInput';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import Pagination from 'dashboard/components/Pagination';
import Permissioned from 'dashboard/components/Permissioned';
import ReactMultiSelect, { flattenOptions } from 'dashboard/components/ReactMultiSelect';
import { generatePlaceHolderDisplay } from 'dashboard/components/ReactMultiSelect/ReactMultiSelect';
import DashboardDatePicker, {
  DateRange,
} from 'dashboard/components-lib/DashboardDatePicker/DashboardDatePicker';
import SearchInput from 'dashboard/components-lib/SearchInput';
import FormAssignCase, { AssignCaseFormData } from 'dashboard/forms/FormAssignCase';
import FormBulkSetCaseState, { BulkSetCaseStateData } from 'dashboard/forms/FormBulkSetCaseState';
import { Container, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { Case } from 'dashboard/models/case';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseTemplateBasicsOption } from 'dashboard/models/case-template';
import { Team } from 'dashboard/models/team';
import { User } from 'dashboard/models/user';
import { CaseQueueCasesFilterParams, FilterValues } from 'dashboard/redux/slices/case-queue';
import { RootState } from 'dashboard/redux/store';
import CasesTable from 'dashboard/views/DashboardCasesView/ConfigurableCasesTable/CasesTable';
import { BulkActionCaseInfo } from 'dashboard/views/DashboardCasesView/DashboardCasesView';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import PageHeader from 'design-system/components/PageHeader';
import Spacer from 'design-system/components/Spacer';
import { HStack, HStackProps } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SearchButtonRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: ${Spacing[4]};
  margin-bottom: ${Spacing[2]};
`;

const StyledButtonGroup = styled(HStack).attrs<HStackProps>({ spacing: Spacing[2] })<HStackProps>`
  margin-top: 28px;
`;

const HARDCODED_COLUMN_OPTIONS = [
  {
    key: 'id',
    label: 'Case ID',
  },
  {
    key: 'assignee',
    label: 'Assignee',
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'active_sla_expires_at',
    label: 'SLA',
  },
  {
    key: 'created_at',
    label: 'Created At',
  },
];

export interface DashboardCaseQueuesShowViewProps {
  activeCaseTemplateBasicsOptions: CaseTemplateBasicsOption[];
  caseQueue: CaseQueue;
  cases: Case[];
  users: User[];
  teams: Team[];
  casesFilters: CaseQueueCasesFilterParams;
  loadingCases: boolean;
  pageSize: number;
  paginationParams: PaginationParams | undefined;
  handleAssigneeFilterUpdate: (
    userAssigneeIds: FilterValues,
    teamAssigneeIds: FilterValues,
  ) => void;
  handleBulkAssign: (caseIds: string[], userId: string, message: string) => void;
  handleBulkSetState: (taskIds: string[], data: BulkSetCaseStateData) => void;
  handleDateUpdate: (range: DateRange) => void;
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
  handleOmniSearch: (omniQuery: string) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleClearFilters: () => void;
  updateCaseQueueName: (name: string) => void;
}

const DashboardCaseQueuesShowView = ({
  activeCaseTemplateBasicsOptions,
  caseQueue,
  cases,
  users,
  teams,
  casesFilters,
  loadingCases,
  pageSize,
  paginationParams,
  handleAssigneeFilterUpdate,
  handleBulkAssign,
  handleBulkSetState,
  handleDateUpdate,
  handleDateUpdateV2,
  handleOmniSearch,
  handlePageLinkClick,
  handlePageSizeClick,
  handleClearFilters,
  updateCaseQueueName,
}: DashboardCaseQueuesShowViewProps) => {
  const [queueName, setQueueName] = useState(caseQueue.attributes.name);
  const [selectedCasesInfo, setSelectedCasesInfo] = useState<BulkActionCaseInfo[]>([]);
  const selectedCaseIds = useMemo(() => {
    return selectedCasesInfo.map((kaseInfo: BulkActionCaseInfo) => kaseInfo.caseId);
  }, [selectedCasesInfo]);

  const [assignModal, { enable: showAssignModal, disable: hideAssignModal }] = useToggle(false);
  const [stateModal, { enable: showStateModal, disable: hideStateModal }] = useToggle(false);

  const canEditQueue = usePermission(Permission.CaseQueueWrite);
  const hasBulkAssignPermission = usePermission(Permission.CaseBulkAssign);
  const hasBulkWritePermission = usePermission(Permission.CaseBulkWrite);

  const selectedCaseTemplateBasicsOption = useMemo(() => {
    const selectedCaseTemplateIds = uniq(
      selectedCasesInfo.map((kaseInfo: BulkActionCaseInfo) => kaseInfo.caseTemplateId),
    );

    return selectedCaseTemplateIds.length === 1
      ? activeCaseTemplateBasicsOptions.find((option) => option.id === selectedCaseTemplateIds[0])
      : undefined;
  }, [activeCaseTemplateBasicsOptions, selectedCasesInfo]);

  const isSelectedOfSameTemplate = selectedCaseTemplateBasicsOption != null;

  const bulkHandleAssign = async (values: AssignCaseFormData) => {
    await handleBulkAssign(selectedCaseIds, values.userId, values.message);
    setSelectedCasesInfo([]);
    hideAssignModal();
  };

  const bulkHandleSetState = async (values: BulkSetCaseStateData) => {
    await handleBulkSetState(selectedCaseIds, values);
    setSelectedCasesInfo([]);
    hideStateModal();
  };

  return (
    <>
      <Container>
        <Helmet title={`${caseQueue.attributes.name} - Queues - Cases`} />
        <PageHeader
          title={
            <DynamicInput
              value={queueName}
              onChange={(e) => {
                setQueueName(e.target.value);
              }}
              onBlur={() => {
                if (queueName !== caseQueue.attributes.name) {
                  updateCaseQueueName(queueName);
                }
              }}
              disabled={!canEditQueue}
            />
          }
          prepend={
            <HorizontalContainer>
              <NavBack
                to='/dashboard/case-queues'
                dataTest='case-assignment-policy-show__header--nav-back'
              />
            </HorizontalContainer>
          }
        />

        <Section>
          <Card>
            <Card.Body>
              <HStack justify='space-between'>
                <Card.Title>{caseQueue.attributes.name}</Card.Title>
                <Button
                  id='queues-show-view__clear-filters'
                  width='auto'
                  variant='inverse'
                  onClick={handleClearFilters}
                >
                  Clear filters
                </Button>
              </HStack>
              <Spacer height={1} />
              <Filters
                filters={casesFilters}
                users={users}
                teams={teams}
                handleAssigneeFilterUpdate={handleAssigneeFilterUpdate}
                handleDateUpdate={handleDateUpdate}
                handleDateUpdateV2={handleDateUpdateV2}
              />
              <Spacer height={1} />
              <SearchButtonRow>
                <SearchInput
                  id='queues-show-view__search'
                  handleInput={handleOmniSearch}
                  value={casesFilters.omni ?? ''}
                  placeholder='Search by case ID, reference ID, account ID, or inquiry ID'
                  size='md'
                />

                <StyledButtonGroup>
                  <Permissioned permission={Permission.CaseBulkWrite}>
                    <Button
                      id='configurable-cases-table__bulk-set-status'
                      onClick={showStateModal}
                      variant='basic'
                      // Disable the button if some selected cases have different templates
                      disabled={selectedCasesInfo.length === 0 || !isSelectedOfSameTemplate}
                    >
                      Set Status ({selectedCasesInfo.length})
                    </Button>
                  </Permissioned>
                  <Permissioned permission={Permission.CaseBulkAssign}>
                    <Button
                      id='configurable-cases-table__bulk-assign'
                      onClick={showAssignModal}
                      variant='basic'
                      disabled={selectedCasesInfo.length === 0}
                    >
                      Assign ({selectedCasesInfo.length})
                    </Button>
                  </Permissioned>
                </StyledButtonGroup>
              </SearchButtonRow>
              <CasesTable
                columnOrdering={HARDCODED_COLUMN_OPTIONS}
                cases={cases}
                caseUsers={users}
                caseCaseQueues={[caseQueue]}
                sortParams={{ sort: '-', shouldSortDescending: true }}
                hasUsedFilters={false}
                hasFilterOnlyOnOpenCases={true}
                isLoading={loadingCases}
                selectedCasesInfo={selectedCasesInfo}
                setSelectedCasesInfo={setSelectedCasesInfo}
                maySelectCases={hasBulkAssignPermission || hasBulkWritePermission}
              />
              {cases.length > 0 && (
                <Pagination
                  pageSize={pageSize}
                  paginationParams={paginationParams}
                  handlePageSizeClick={handlePageSizeClick}
                  handlePageLinkClick={handlePageLinkClick}
                />
              )}
            </Card.Body>
          </Card>
        </Section>
      </Container>
      <ModalWrapper isOpen={stateModal} handleCloseModal={hideStateModal}>
        <ModalContainer data-test='bulk-set-state-case-modal'>
          <ModalHeader data-test='bulk-set-state-case-modal__header' onClose={hideStateModal}>
            Set Status
          </ModalHeader>

          <ModalBody>
            <FormBulkSetCaseState
              handleCancel={hideStateModal}
              onSubmit={bulkHandleSetState}
              selectedStates={selectedCasesInfo.map((info) => info.state)}
              caseTemplateStates={selectedCaseTemplateBasicsOption?.states ?? []}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <ModalWrapper isOpen={assignModal} handleCloseModal={hideAssignModal}>
        <ModalContainer data-test='bulk-assign-case-modal'>
          <ModalHeader data-test='bulk-assign-case-modal__header' onClose={hideAssignModal}>
            Assign {selectedCasesInfo.length} {selectedCasesInfo.length === 1 ? 'Case' : 'Cases'}
          </ModalHeader>

          <ModalBody>
            <FormAssignCase
              handleCancel={hideAssignModal}
              onSubmit={bulkHandleAssign}
              users={users}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

const FilterRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: ${Spacing[4]};
  row-gap: ${Spacing[2]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
  }
`;

const LargeFilterItem = styled('div')`
  @media ${mediaQuery({ max: MEDIA_WIDTHS.fullHD })} {
    grid-column: span 2;
  }
`;

interface FiltersProps {
  filters: CaseQueueCasesFilterParams;
  users: User[];
  teams: Team[];
  handleAssigneeFilterUpdate: (
    userAssigneeIds: FilterValues,
    teamAssigneeIds: FilterValues,
  ) => void;
  handleDateUpdate: (range: DateRange) => void;
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
}

const Filters = ({
  filters,
  users,
  teams,
  handleAssigneeFilterUpdate,
  handleDateUpdateV2,
}: FiltersProps) => {
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);

  const userOptions = useMemo(() => {
    const unassigned = [
      {
        label: 'Unassigned',
        value: 'unassigned',
      },
    ];

    const myUser = users.some((user: User) => user.id === currentUser.id)
      ? [
          {
            label: `${userDisplayName(currentUser)} (Assigned to me)`,
            value: currentUser.id,
          },
        ]
      : [];

    const otherUsers = users.map((user) => {
      return {
        label: user.attributes['email-address'],
        value: user.id,
      };
    });
    return [...myUser, ...unassigned, ...otherUsers];
  }, [currentUser, users]);

  const teamOptions = useMemo(
    () =>
      teams.map((team) => {
        return {
          label: team.attributes['name'],
          value: team.id,
        };
      }),
    [teams],
  );

  const assigneeOptions = [
    {
      label: 'Teams',
      value: 'teams',
      options: teamOptions,
    },
    {
      label: 'Users',
      value: 'users',
      options: userOptions,
    },
  ];

  return (
    <FilterRow>
      <div>
        <Label>Date range</Label>
        <DashboardDatePicker
          dateRange={{
            startDate: filters.startDate,
            endDate: filters.endDate,
          }}
          handleDatesChange={handleDateUpdateV2}
          label=''
          size='md'
        />
      </div>
      <LargeFilterItem>
        <Label id='queue-cases-table__select-assignee-ids-label'>Assigned to</Label>
        <ReactMultiSelect
          portal
          isMulti
          aria-labelledby='queue-cases-table__select-assignee-ids-label'
          options={assigneeOptions}
          searchEnabled
          selectAllEnabled
          onChange={(assigneeIds: string[], isSelectAll: boolean | undefined) => {
            if (isSelectAll) {
              handleAssigneeFilterUpdate('all', 'all');
            } else {
              const teamIds = assigneeIds.filter((resolverId) => resolverId.startsWith('team_'));
              const userIds = assigneeIds.filter((resolverId) => !resolverId.startsWith('team_'));
              handleAssigneeFilterUpdate(userIds, teamIds);
            }
          }}
          value={
            filters.teamAssigneeIds === 'all'
              ? flattenOptions(assigneeOptions).map((option) => option.value)
              : [...filters.teamAssigneeIds, ...filters.assigneeIds]
          }
          placeholderGenerator={generatePlaceHolderDisplay({
            label: { single: 'assignee', multiple: 'assignees' },
            emptyIsAll: true,
          })}
        />
      </LargeFilterItem>
    </FilterRow>
  );
};

export default DashboardCaseQueuesShowView;
